import { Link } from "react-router-dom";
import type { LegalContentProps } from "@/legal/legalTypes";

export const TermsEnContent = ({ company, contactEmail }: LegalContentProps) => (
  <>
    <h1 className="text-3xl font-display font-bold text-foreground mb-8">Terms of Use</h1>
    <div className="prose prose-sm text-muted-foreground font-body space-y-6">
      <p className="text-foreground font-medium">Last updated: April 27, 2026.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">1. Service provider information</h2>
      <p>
        The "Thenea" service is provided by {company.name}{company.address ? `, registered at ${company.address}` : ""}
        {company.oib ? `, OIB: ${company.oib}` : ""}{company.mbs ? `, registration number: ${company.mbs}` : ""}.
        Contact: <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">{contactEmail}</a>
        {company.phone ? `, phone: ${company.phone}` : ""}.
      </p>

      <h2 className="text-xl font-display font-semibold text-foreground">2. Acceptance of terms</h2>
      <p>By using Thenea (the "Service"), you accept these Terms of Use in full. If you do not agree with these terms, please do not use the Service. Continued use after updates means acceptance of the updated terms.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">3. Service description</h2>
      <p>Thenea provides a platform for shared photo and video galleries for events such as weddings, celebrations, and similar gatherings. Guests can share photos and videos using a QR code without installing an app.</p>
      <p>Additional features may include a social feed with likes/comments, voice guestbook, ZIP album download, and NFC access.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">4. Registration and ordering</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>To use the Service, an event must be created on our website.</li>
        <li>By ordering a package, you agree to these Terms and our <Link to="/privatnost" className="text-primary hover:underline">Privacy Policy</Link>.</li>
        <li>An order becomes binding after provider confirmation.</li>
        <li>Guests do not need user accounts and can enter via QR code or link.</li>
      </ul>

      <h2 className="text-xl font-display font-semibold text-foreground">5. User content</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>You are responsible for all content uploaded by you or your guests.</li>
        <li>Uploading illegal, abusive, discriminatory, pornographic, or rights-infringing content is prohibited.</li>
        <li>We reserve the right to remove content that violates these terms without prior notice.</li>
        <li>We are not responsible for guest-uploaded content.</li>
      </ul>

      <h2 className="text-xl font-display font-semibold text-foreground">6. Intellectual property</h2>
      <p>You retain ownership and copyright of uploaded photos, videos, and voice messages. By using the Service, you grant us a limited, non-exclusive license to store, process, and display this content solely to provide the Service.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">7. Pricing and packages</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>Packages vary by photo limits, retention period, and available add-ons.</li>
        <li>Premium add-ons are billed according to the active price list.</li>
        <li>All listed prices include VAT where applicable.</li>
        <li>Prices may change; confirmed orders keep their confirmed price.</li>
      </ul>

      <h2 className="text-xl font-display font-semibold text-foreground">8. Data retention and deletion</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>Retention depends on selected package duration.</li>
        <li>After retention expires, data is automatically deleted after an additional 60-day grace period.</li>
        <li>Users may request retention extension before expiry.</li>
      </ul>

      <h2 className="text-xl font-display font-semibold text-foreground">9. Business discontinuation</h2>
      <div className="bg-secondary/50 border border-border rounded-xl p-4">
        <p className="text-foreground font-medium mb-2">Important — your data protection:</p>
        <p>If {company.name} stops operations, we commit to notify active users and deliver remaining media to the contact email (where feasible) before final deletion.</p>
      </div>

      <h2 className="text-xl font-display font-semibold text-foreground">10. Service availability</h2>
      <p>We aim for high availability, but uninterrupted operation is not guaranteed due to maintenance and technical constraints.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">11. Limitation of liability</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>Maximum liability is limited to the amount paid for the Service.</li>
        <li>We are not liable for third-party guest content.</li>
      </ul>

      <h2 className="text-xl font-display font-semibold text-foreground">12. Withdrawal rights (consumers)</h2>
      <p>Consumer withdrawal rights may apply according to applicable law, except where digital services have already been provided with explicit consent.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">13. Prohibited behavior</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>Use of the Service for unlawful purposes.</li>
        <li>Unauthorized access attempts to other galleries or data.</li>
        <li>Automated scraping or abuse of infrastructure.</li>
      </ul>

      <h2 className="text-xl font-display font-semibold text-foreground">14. Termination</h2>
      <p>We may suspend or terminate access for terms violations. You may stop using the Service at any time by contacting us at <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">{contactEmail}</a>.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">15. Updates to terms</h2>
      <p>We may update these terms from time to time and notify users of significant changes.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">16. Data protection</h2>
      <p>Personal data processing is described in our <Link to="/privatnost" className="text-primary hover:underline">Privacy Policy</Link>, which forms part of these Terms.</p>
      <p>The Service uses analytics and conversion measurement tools (Google Analytics 4, Google Ads, Meta Pixel, TikTok Pixel) to improve the service and measure advertising performance. These tools may process aggregated, non-personal usage and conversion data as described in the Privacy Policy. All such processing is subject to your consent choices.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">17. Governing law and jurisdiction</h2>
      <p>These terms are governed by Croatian law. Competent courts in Zagreb have jurisdiction unless mandatory law provides otherwise.</p>

      <h2 className="text-xl font-display font-semibold text-foreground">18. Contact</h2>
      <p>For questions regarding these terms, contact us at <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">{contactEmail}</a>{company.phone ? ` or phone: ${company.phone}` : ""}.</p>
    </div>
  </>
);
